import { animated } from '#app/components/Primitives/Animated.js';
import { type Sortable, type SortableListItemProps } from './types.js';
import { useSortable } from '@dnd-kit/sortable';
import { cloneElement } from 'react';
import { css, styled } from 'styled-components';

/**
 * touch-action: none should be applied to elements that are being sorted.
 * Preferably, elements would be sorted by a drag handle and that would have touch-action
 * applied. In certain conditions, (i.e, add links screen) where the whole element is sortable/draggable,
 * you'll want to apply touch-action: none to the element itself.
 *
 * See: https://docs.dndkit.com/api-documentation/draggable#touch-action
 */
const StyleSortableListItem = styled(animated.div)<{
  isDragging: boolean;
  isSorting: boolean;
}>`
  ${({ isDragging, isSorting }) => css`
    z-index: auto;

    ${isDragging &&
    css`
      z-index: 2;
      cursor: grabbing;
    `}

    ${!isSorting &&
    css`
      transform: none !important;
    `}
  `}
`;

export const SortableListItemWithClonedElement = ({
  activeSortId,
  children,
  disabled = true,
  scaleOnDrag = true,
  sortId,
  ...props
}: SortableListItemProps) => {
  const { attributes, isDragging, listeners, setNodeRef, transform } =
    useSortable({ id: sortId, transition: null });

  const isSortActive = activeSortId === sortId;

  const cloneProps: Sortable = {
    disabled,
    isDragging,
    isSortActive,
    sortableListeners: listeners,
    ...props,
  };

  const itemProps = disabled ? {} : attributes;

  const initial = { scale: scaleOnDrag ? 1 : undefined, x: 0, y: 0 };
  const animate = transform
    ? {
        scale: isDragging && scaleOnDrag ? 1.1 : 1,
        x: transform.x,
        y: transform.y,
      }
    : initial;

  const transition = {
    duration: isDragging ? 0 : 0.25,
    // cspell:disable-next-line
    easings: {
      damping: 30,
      stiffness: 100,
      type: 'spring',
    },
    scale: scaleOnDrag
      ? {
          duration: 0.25,
        }
      : undefined,
  };

  return (
    <StyleSortableListItem
      animate={animate}
      initial={initial}
      isDragging={isDragging}
      isSorting={Boolean(activeSortId)}
      layoutId={String(sortId)}
      ref={setNodeRef}
      transition={transition}
      {...itemProps}
      tabIndex={-1}
    >
      {disabled ? children : cloneElement(children, cloneProps)}
    </StyleSortableListItem>
  );
};
